import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";
import StatusBadge from "../components/StatusBadge";
import StatCard from "../components/StatCard";

interface Device {
  id: string;
  model: string;
  serial: string;
  status: "available" | "busy" | "offline" | "claimed" | "installing" | "ready";
  host: string;
  platform: string;
  osVersion: string;
  currentJob?: {
    runId: string;
    mrLink: string;
    suite: string;
  };
}


interface DevicesResponse {
  devices: Device[];
  total: number;
}

export default function FleetOverview() {
  const { data, loading, error, refetch } = useApi<DevicesResponse>("/api/devices");
  const devices = data?.devices ?? [];
  useSSERefetch("/api/events", ["host:heartbeat", "device:status", "device:claimed", "device:released"], refetch);

  const [statusFilter, setStatusFilter] = useState("");
  const [hostFilter, setHostFilter] = useState("");
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  // SSE triggers refetch automatically via useSSERefetch
  const mergedDevices = devices;

  // Derive unique hosts for filter dropdown
  const hosts = useMemo(() => {
    const set = new Set(mergedDevices.map((d) => d.host));
    return Array.from(set).sort();
  }, [mergedDevices]);

  // Apply filters
  const filtered = useMemo(() => {
    return mergedDevices.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false;
      if (hostFilter && d.host !== hostFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !d.model.toLowerCase().includes(q) &&
          !d.serial.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [mergedDevices, statusFilter, hostFilter, search]);

  // Stat counts
  const stats = useMemo(() => {
    const total = mergedDevices.length;
    const available = mergedDevices.filter((d) => d.status === "available").length;
    const busy = mergedDevices.filter((d) => d.status === "busy").length;
    const offline = mergedDevices.filter((d) => d.status === "offline").length;
    const installing = mergedDevices.filter((d) => d.status === "claimed" || d.status === "installing").length;
    return { total, available, busy, offline, installing };
  }, [mergedDevices]);

  if (loading && !devices) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading devices...</p>;
  }

  if (error) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        Error: {error}
      </p>
    );
  }

  return (
    <div>
      <h1 style={pageTitleStyle}>Fleet Overview</h1>

      {/* Stat Cards */}
      <section style={statCardsStyle}>
        <StatCard label="Total Devices" value={stats.total} color={statColors.total} />
        <StatCard label="Available" value={stats.available} color={statColors.available} />
        <StatCard label="Busy" value={stats.busy} color={statColors.busy} />
        <StatCard label="Installing" value={stats.installing} color={statColors.installing} />
        <StatCard label="Offline" value={stats.offline} color={statColors.offline} />
      </section>

      {/* Filter Bar */}
      <section style={filterBarStyle}>
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={filterInputStyle}
        >
          <option value="">All Statuses</option>
          <option value="available">Available</option>
          <option value="busy">Busy</option>
          <option value="claimed">Claimed</option>
          <option value="installing">Installing</option>
          <option value="ready">Ready</option>
          <option value="offline">Offline</option>
        </select>

        <select
          aria-label="Filter by host"
          value={hostFilter}
          onChange={(e) => setHostFilter(e.target.value)}
          style={filterInputStyle}
        >
          <option value="">All Hosts</option>
          {hosts.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>

        <input
          type="search"
          placeholder="Search by model or serial..."
          aria-label="Search devices"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...filterInputStyle, flex: 1, minWidth: 180 }}
        />
      </section>

      {/* Device Grid */}
      <section
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, minmax(var(--device-card-min-width), 1fr))`,
          gap: "var(--space-md)",
        }}
      >
        {filtered.map((device) => (
          <DeviceCard
            key={device.id}
            device={device}
            onClick={() => navigate(`/devices/${device.id}`)}
          />
        ))}
        {filtered.length === 0 && (
          <p style={{ color: "var(--color-text-secondary)", gridColumn: "1 / -1" }}>
            No devices match filters.
          </p>
        )}
      </section>
    </div>
  );
}

/* ---- Sub-components ---- */

// Border-top and value color were identical per variant in the old inline
// StatCard, so the shared component (src/components/StatCard.tsx) takes a
// single `color` prop covering both.
const statColors: Record<string, string> = {
  total: "var(--color-info)",
  available: "var(--color-success)",
  busy: "var(--color-warning)",
  installing: "var(--color-warning)",
  offline: "var(--color-danger)",
};

function DeviceCard({
  device,
  onClick,
}: {
  device: Device;
  onClick: () => void;
}) {
  const borderColors: Record<string, string> = {
    available: "var(--color-success)",
    busy: "var(--color-warning)",
    claimed: "var(--color-status-claimed-text)",
    installing: "var(--color-warning)",
    ready: "var(--color-status-ready-text)",
    offline: "var(--color-danger)",
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)",
        borderLeft: `4px solid ${borderColors[device.status] ?? "transparent"}`,
        cursor: "pointer",
        transition: "box-shadow 0.15s, transform 0.1s",
        textDecoration: "none",
        color: "inherit",
        display: "block",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "var(--space-sm)",
        }}
      >
        <span style={{ fontSize: "var(--font-size-md)", fontWeight: 600 }}>
          {device.model}
        </span>
        <StatusBadge status={device.status} />
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--font-size-xs)",
          color: "var(--color-text-secondary)",
          marginBottom: "var(--space-sm)",
        }}
      >
        {device.serial}
      </div>
      {device.status === "installing" && (
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-status-installing-text)", fontWeight: 600, marginBottom: "var(--space-sm)" }}>
          Installing APK...
        </div>
      )}
      {device.status === "ready" && (
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-status-ready-text)", fontWeight: 600, marginBottom: "var(--space-sm)" }}>
          Ready
        </div>
      )}
      {device.status === "claimed" && (
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-status-claimed-text)", fontWeight: 600, marginBottom: "var(--space-sm)" }}>
          Claimed
        </div>
      )}
      <div
        style={{
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
        }}
      >
        {device.host} &middot; {device.platform} {device.osVersion}
      </div>
      {device.currentJob && (
        <div
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
            marginTop: "var(--space-sm)",
            paddingTop: "var(--space-sm)",
            borderTop: "var(--border-default)",
          }}
        >
          <span style={{ color: "var(--color-info)" }}>{device.currentJob.mrLink}</span>{" "}
          &middot; {device.currentJob.suite}
        </div>
      )}
    </div>
  );
}

/* ---- Inline Styles ---- */

const pageTitleStyle: React.CSSProperties = {
  fontSize: "var(--font-size-xl)",
  fontWeight: 700,
  marginBottom: "var(--space-xl)",
  color: "var(--color-primary)",
};

const statCardsStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: "var(--space-lg)",
  marginBottom: "var(--space-xl)",
};

const filterBarStyle: React.CSSProperties = {
  display: "flex",
  gap: "var(--space-md)",
  marginBottom: "var(--space-xl)",
  flexWrap: "wrap",
  alignItems: "center",
};

const filterInputStyle: React.CSSProperties = {
  fontSize: "var(--font-size-base)",
  padding: "var(--space-sm) var(--space-md)",
  border: "var(--border-default)",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-surface)",
  color: "var(--color-text-primary)",
  minHeight: "var(--touch-target-min)",
  outline: "none",
};
